import React, { useState, type ReactNode, useEffect } from 'react';
import { Layout, Menu, Button, Typography, Dropdown, type MenuProps, Drawer, Grid } from 'antd';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  DashboardOutlined,
  TeamOutlined,
  ShopOutlined,
  DollarCircleOutlined,
  LineChartOutlined, // For Sales parent item
  AreaChartOutlined, // For Sales Dashboard specific icon
  ProjectOutlined, // Generic icon for sub-items
  DollarOutlined,
  CustomerServiceOutlined,
  LogoutOutlined,
  UserOutlined,
  CompassOutlined, // For Discover page
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import LanguageSelector from '../components/LanguageSelector'; // Uncommented
import { useAuth } from '../contexts/AuthContext';
import BrandLogo from '../components/common/BrandLogo';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

interface AdminLayoutProps {
  children: ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    if (isMobile) {
      setCollapsed(true);
      setMobileOpen(false);
    } else {
      setCollapsed(false);
      setMobileOpen(false);
    }
  }, [isMobile]);

  const menuItems = [
    { key: '/', icon: <DashboardOutlined />, label: t('navigation.dashboard') },
    { key: '/discover', icon: <CompassOutlined />, label: t('navigation.discover') },
    { key: '/sales/administrator-leads', icon: <ProjectOutlined />, label: t('navigation.administratorLeads') },
    {
      key: '_sales_group',
      icon: <LineChartOutlined />,
      label: t('navigation.sales'),
      children: [
        { key: '/sales', icon: <AreaChartOutlined />, label: t('navigation.salesDashboard') },
        { key: '/sales/leads', icon: <ProjectOutlined />, label: t('navigation.leads') },
        { key: '/sales/opportunities', icon: <ProjectOutlined />, label: t('navigation.opportunities') },
        { key: '/sales/customers', icon: <ProjectOutlined />, label: t('navigation.customers') },
      ],
    },
    {
      key: '/finance',
      icon: <DollarOutlined />,
      label: t('navigation.finance'),
      children: [
        {
          key: '/finance/price-plans',
          icon: <DollarOutlined />,
          label: t('navigation.price_plans'),
        },
        {
          key: '/finance/billing',
          icon: <DollarCircleOutlined />,
          label: t('navigation.billing'),
        },
      ],
    },
    { key: '/workspaces', icon: <ShopOutlined />, label: t('navigation.workspaces') },
    { key: '/support', icon: <CustomerServiceOutlined />, label: t('navigation.support') },
    { key: '/users', icon: <TeamOutlined />, label: t('navigation.users') },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const getSelectedKeys = () => {
    const currentPath = location.pathname;
    let bestMatchKey = '/';
    let parentKey: string | null = null;

    // Highlight /users for any /users or /users/:id route
    if (currentPath.startsWith('/users')) {
      bestMatchKey = '/users';
      parentKey = null;
      return [bestMatchKey];
    }

    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (child.key === currentPath) {
            bestMatchKey = child.key;
            parentKey = item.key;
            break;
          }
        }
      } else {
        if (item.key === currentPath) {
          bestMatchKey = item.key;
          break;
        }
      }
      if (bestMatchKey !== '/' && parentKey) break;
    }
    
    if (bestMatchKey === '/' && currentPath.startsWith('/sales/') && currentPath !== '/sales') {
        const parts = currentPath.split('/');
        if (parts.length > 2) {
            const baseSalesPath = `/${parts[1]}/${parts[2]}`;
            if (baseSalesPath === '/sales/opportunities') {
              bestMatchKey = '/sales/opportunities';
              parentKey = '_sales_group';
            } else {
            for (const item of menuItems) {
                if(item.children){
                    for(const child of item.children){
                        if(child.key === baseSalesPath){
                            bestMatchKey = child.key;
                            parentKey = item.key;
                            break;
                        }
                    }
                }
                if (bestMatchKey !== '/' && parentKey) break;
            }
            }
        }
    }
    // Highlight /workspaces for any /workspaces or /workspaces/:id route
    if (currentPath.startsWith('/workspaces')) {
      bestMatchKey = '/workspaces';
      parentKey = null;
    }

    return parentKey ? [bestMatchKey, parentKey] : [bestMatchKey];
  };

  const getOpenKeys = () => {
    const currentPath = location.pathname;
    if (currentPath.startsWith('/sales')) {
      const salesGroup = menuItems.find(item => item.children && item.children.some(child => child.key.startsWith('/sales')));
      if (salesGroup) return [salesGroup.key];
    }
    return [];
  };
  
  const getCurrentPageTitle = () => {
    const selectedKeys = getSelectedKeys();
    const primarySelectedKey = selectedKeys[0];

    const findLabel = (items: typeof menuItems, key: string): string | null => {
      for (const item of items) {
        if (item.key === key) return item.label;
        if (item.children) {
          const childLabel = findLabel(item.children, key);
          if (childLabel) return childLabel;
        }
      }
      return null;
    };
    return findLabel(menuItems, primarySelectedKey) || t('navigation.dashboard');
  };

  // Logout handler
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const dropdownMenu: MenuProps = {
    items: [
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('logout', 'Logout'),
        onClick: handleLogout,
      },
    ],
  };

  const drawerContent = (
    <>
      <div style={{ height: '64px', margin: '0px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#001529', flexShrink: 0 }}>
        {!isMobile && !collapsed && (
          <BrandLogo variant="sidebar" logoHeight={40} showTitle={true} showTagline={true} />
        )}
        {!isMobile && collapsed && (
          <BrandLogo variant="sidebar" logoHeight={32} showTitle={false} />
        )}
        {isMobile && (
          <BrandLogo variant="sidebar" logoHeight={40} showTitle={true} showTagline={true} />
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKeys().slice(0,1)}
          defaultOpenKeys={getOpenKeys()}
          onClick={handleMenuClick}
          items={menuItems}
        />
      </div>
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          title={null}
          placement="left"
          closable={false}
          onClose={() => setMobileOpen(false)}
          open={mobileOpen}
          width={240}
          styles={{ body: { padding: 0, background: '#001529' } }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
          }}
          width={240}
        >
          {drawerContent}
        </Sider>
      )}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : (collapsed ? 80 : 240),
          transition: 'margin-left 0.2s',
          overflowX: 'hidden',
        }}
      >
        <Header
          style={{
            padding: isMobile ? '0 8px' : '0 16px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 99,
            height: isMobile ? '48px' : '64px'
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={handleDrawerToggle}
            style={{
              fontSize: '16px',
              width: isMobile ? 48 : 64,
              height: isMobile ? 48 : 64,
            }}
          />
          <Title level={4} style={{ margin: 0, fontSize: isMobile ? '16px' : '20px' }}>
            {getCurrentPageTitle()}
          </Title>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 }}>
            <LanguageSelector />
            {user && (
              <Dropdown menu={dropdownMenu} placement="bottomRight" trigger={["click"]}>
                <Button type="text" icon={<UserOutlined />} style={{ padding: '0 8px' }} />
              </Dropdown>
            )}
          </div>
        </Header>
        <Content
          style={{
            padding: 24,
            minHeight: 280,
            background: '#fff', 
            flexGrow: 1,
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default React.memo(AdminLayout); 